import { FC, useCallback } from "react";
import { Button } from "antd";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { EOrderStatus, IOrder } from "../types";
import { processRefunds, updateOrderStatus } from "../api";
import { refreshOrder } from "../slice";
import { AppDispatch } from "../../../app/store";
import { useAppNotifications, getErrorMessage } from "../../../components/LMNotifications";
import styles from "./OrderActions.module.css";

interface Props {
  order: IOrder;
}

export const OrderActions: FC<Props> = ({ order }) => {
  const { t } = useTranslation("order");
  const dispatch = useDispatch<AppDispatch>();
  const { contextHolder, success, error } = useAppNotifications();

  const getOrderActions = useCallback(() => {
    const actions: Record<string, EOrderStatus> = {};
    if (order.status === EOrderStatus.Payment) {
      actions["payment-queued-label"] = EOrderStatus.Queued;
    }
    if (order.status === EOrderStatus.Pending) {
      actions["pending-queued-label"] = EOrderStatus.Queued;
    }
    if (order.status === EOrderStatus.Ready) {
      actions["ready-delivering-label"] = EOrderStatus.Delivering;
    }
    if (order.status === EOrderStatus.Error) {
      actions["error-pending-label"] = EOrderStatus.Error;
      actions["error-queued-label"] = EOrderStatus.Queued;
    }
    if (order.status !== EOrderStatus.Cancelled) {
      actions["cancelled-label"] = EOrderStatus.Cancelled;
    }
    return actions;
  }, [order]);

  const handleUpdateStatus = useCallback(
    async (action: EOrderStatus) => {
      await updateOrderStatus(order.id, action);
      dispatch(refreshOrder({ orderId: order.id }));
    },
    [order, dispatch]
  );

  const handleProcessRefunds = useCallback(async () => {
    try {
      await processRefunds(order.id);
      dispatch(refreshOrder({ orderId: order.id }));
      success(t("processRefundsSuccess"));
    } catch (e) {
      error(t("processRefundsError"), getErrorMessage(e));
    }
  }, [order, dispatch, success, error, t]);

  const actions = getOrderActions();

  return (
    <div className={styles.container}>
      {contextHolder}
      <Button onClick={handleProcessRefunds}>{t("process-refunds")}</Button>
      {Object.keys(actions).map((label) => {
        const status = actions[label];
        return (
          <Button onClick={() => handleUpdateStatus(status)}>{t(label)}</Button>
        );
      })}
    </div>
  );
};
